// Settings service to read configuration from API
class SettingsService {
    private cache: Map<string, any> = new Map();
    private cacheExpiry: Map<string, number> = new Map();
    private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
    private readonly MAX_RETRIES = 2;
    private readonly RETRY_DELAY = 1000; // 1 second

    // Helper for retry with exponential backoff
    private async fetchWithRetry(url: string, options?: RequestInit, retries = this.MAX_RETRIES): Promise<Response> {
        let lastError: Error | null = null;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                const response = await fetch(url, options);
                return response;
            } catch (error) {
                lastError = error as Error;
                if (attempt < retries) {
                    // Exponential backoff: 1s, 2s, 4s...
                    await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * Math.pow(2, attempt)));
                }
            }
        }
        throw lastError;
    }

    async getSetting<T = any>(key: string, defaultValue: T): Promise<T> {
        // Check cache first
        if (this.cache.has(key)) {
            const expiry = this.cacheExpiry.get(key) || 0;
            if (Date.now() < expiry) {
                return this.cache.get(key) as T;
            }
        }

        try {
            const response = await this.fetchWithRetry(`/api/settings/${key}`);
            if (!response.ok) {
                console.warn(`Setting '${key}' not found, using default:`, defaultValue);
                return defaultValue;
            }

            const data = await response.json();
            if (!data.success) {
                console.warn(`Failed to fetch setting '${key}', using default:`, defaultValue);
                return defaultValue;
            }

            let value = data.setting.value;

            // Parse JSON value if it's stored as JSON
            if (typeof value === 'string' && data.setting.dataType !== 'string') {
                try {
                    value = JSON.parse(value);
                } catch (e) {
                    console.warn(`Failed to parse setting '${key}' as JSON:`, e);
                }
            }

            // Cache the value
            this.cache.set(key, value);
            this.cacheExpiry.set(key, Date.now() + this.CACHE_TTL);

            return value as T;
        } catch (error) {
            console.error(`Error fetching setting '${key}':`, error);
            return defaultValue;
        }
    }

    async getMultipleSettings(keys: string[]): Promise<Record<string, any>> {
        const settings: Record<string, any> = {};

        try {
            const response = await this.fetchWithRetry('/api/settings/batch', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ keys })
            });

            if (!response.ok) {
                console.warn('Failed to fetch multiple settings');
                return settings;
            }

            const data = await response.json();

            for (const item of data.settings) {
                let value = item.value;

                // Parse JSON value if needed
                if (typeof value === 'string' && item.data_type !== 'string') {
                    try {
                        value = JSON.parse(value);
                    } catch (e) {
                        console.warn(`Failed to parse setting '${item.key}' as JSON:`, e);
                    }
                }

                settings[item.key] = value;

                // Cache individual values
                this.cache.set(item.key, value);
                this.cacheExpiry.set(item.key, Date.now() + this.CACHE_TTL);
            }
        } catch (error) {
            console.warn('Error fetching multiple settings:', error);
        }

        return settings;
    }

    async updateSetting(key: string, value: any): Promise<boolean> {
        try {
            const response = await fetch(`/api/settings/${key}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ value })
            });

            if (!response.ok) {
                console.error(`Failed to update setting '${key}'`);
                return false;
            }

            const data = await response.json();
            if (!data.success) {
                console.error(`Failed to update setting '${key}':`, data.error);
                return false;
            }

            // Clear cache for this key
            this.clearCache(key);

            return true;
        } catch (error) {
            console.error(`Error updating setting '${key}':`, error);
            return false;
        }
    }

    async getAllSettings(category?: string): Promise<Record<string, any>> {
        try {
            const url = category ? `/api/settings?category=${category}` : '/api/settings';
            const response = await fetch(url);

            if (!response.ok) {
                console.warn('Failed to fetch all settings');
                return {};
            }

            const data = await response.json();
            if (!data.success) {
                console.warn('Failed to fetch all settings:', data.error);
                return {};
            }

            const settings: Record<string, any> = {};
            data.settings.forEach((setting: any) => {
                settings[setting.key] = setting.value;
            });

            return settings;
        } catch (error) {
            console.error('Error fetching all settings:', error);
            return {};
        }
    }

    clearCache(key?: string) {
        if (key) {
            this.cache.delete(key);
            this.cacheExpiry.delete(key);
        } else {
            this.cache.clear();
            this.cacheExpiry.clear();
        }
    }
}

// Create singleton instance
export const settingsService = new SettingsService();

// Helper functions for commonly used settings
export async function getValidationRules() {
    const keys = [
        'validation_email_regex',
        'validation_phone_regex',
        'validation_min_name_length',
        'validation_max_name_length',
        'validation_min_address_length',
        'validation_max_address_length'
    ];

    const settings = await settingsService.getMultipleSettings(keys);

    return {
        EMAIL_REGEX: new RegExp(settings.validation_email_regex || '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$'),
        PHONE_REGEX: new RegExp(settings.validation_phone_regex || '^(\\+61|0)[2-9]\\d{8}$'),
        MIN_NAME_LENGTH: settings.validation_min_name_length || 2,
        MAX_NAME_LENGTH: settings.validation_max_name_length || 50,
        MIN_ADDRESS_LENGTH: settings.validation_min_address_length || 10,
        MAX_ADDRESS_LENGTH: settings.validation_max_address_length || 200
    };
}

export async function getAppConfig() {
    const keys = [
        'app_name',
        'company_name',
        'contact_email',
        'contact_phone',
        'default_booking_duration',
        'booking_slots_per_day',
        'max_booking_days_ahead',
        'session_timeout'
    ];

    const settings = await settingsService.getMultipleSettings(keys);

    return {
        APP_NAME: settings.app_name || 'Alpha Air Self-Service',
        COMPANY_NAME: settings.company_name || 'Alpha Air',
        CONTACT_EMAIL: settings.contact_email || 'info@alphaair.com.au',
        CONTACT_PHONE: settings.contact_phone || '1300864703',
        DEFAULT_BOOKING_DURATION: settings.default_booking_duration || 60,
        BOOKING_SLOTS_PER_DAY: settings.booking_slots_per_day || 16,
        MAX_BOOKING_DAYS_AHEAD: settings.max_booking_days_ahead || 30,
        SESSION_TIMEOUT: settings.session_timeout || (30 * 60 * 1000)
    };
}